import { useScrollAnimation } from "../../../hooks/useScrollAnimation";
import { products } from "../../../mocks/products";

interface ProductGalleryProps {
  onNavigate: (id: string) => void;
}

function ProductCard({
  product,
  delay,
  onNavigate,
}: {
  product: (typeof products)[0];
  delay: number;
  onNavigate: (id: string) => void;
}) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`group bg-white transition-all duration-1000 cursor-pointer rounded-sm`}
      style={{
        transitionDelay: `${delay}ms`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
      }}
    >
      {/* Image */}
      <div className="relative overflow-hidden w-full h-[260px] sm:h-[300px] md:h-[360px]">
        <img
          src={product.image}
          alt={product.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#3D2817]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>

      {/* Text Area */}
      <div className="p-5 sm:p-6 md:p-8">
        <p
          className="text-[#B76E79] text-[10px] uppercase tracking-[3px] mb-2 sm:mb-3"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {product.category}
        </p>
        <h3
          className="text-[#6B4423] text-lg sm:text-xl mb-2 sm:mb-3"
          style={{ fontFamily: "Cinzel, serif", fontWeight: 400 }}
        >
          {product.title}
        </h3>
        <p
          className="text-[#7A6F5D] text-sm leading-relaxed mb-4 sm:mb-5"
          style={{ fontFamily: "Inter, sans-serif", fontWeight: 300 }}
        >
          {product.description}
        </p>
        <div className="flex items-center justify-between">
          <span
            className="text-[#6B4423] text-sm"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
          >
            {product.price}
          </span>
          <button
            onClick={() => onNavigate("custom")}
            className="flex items-center gap-2 text-[#B76E79] text-[11px] uppercase tracking-[2px] group/btn cursor-pointer whitespace-nowrap hover:text-[#6B4423] transition-colors duration-300"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            View Piece
            <span className="transform group-hover/btn:translate-x-1 transition-transform duration-300">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProductGallery({ onNavigate }: ProductGalleryProps) {
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <section id="collection" className="bg-[#FAF8F3] py-20 md:py-40" data-product-shop>
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 md:px-10">
        {/* Header */}
        <div
          ref={headRef}
          className={`text-center mb-12 md:mb-20 transition-all duration-1000 ${
            headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p
            className="text-[#B76E79] text-[11px] uppercase tracking-[4px] mb-4 sm:mb-5"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            The Collection
          </p>
          <h2
            className="text-[#6B4423] text-4xl sm:text-5xl"
            style={{ fontFamily: "Cinzel, serif", fontWeight: 400 }}
          >
            Every Piece, A Story
          </h2>
          <div className="w-16 h-px bg-[#B76E79]/40 mx-auto mt-6 sm:mt-8" />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 md:gap-8">
          {products.map((product, i) => (
            <ProductCard
              key={product.id}
              product={product}
              delay={i * 150}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
